import React, { useState, useEffect } from 'react';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from './ui/dialog';
import { Button } from './ui/button';
import { Badge } from './ui/badge';
import { Mail, Inbox, ArrowLeft, Clock, CheckCircle2, Circle, Trash2, PartyPopper, X, ArrowUpRight } from 'lucide-react';
import { getMockInbox, markEmailAsRead, deleteEmail } from '../utils/registrationUtils';

const MockInboxLight = ({ isOpen, onClose, onTRNEmailViewed, onInboxCloseAfterTRN }) => {
  const [emails, setEmails] = useState([]);
  const [selectedEmail, setSelectedEmail] = useState(null);
  const [showTRNTooltip, setShowTRNTooltip] = useState(false);
  const [trnWasViewed, setTrnWasViewed] = useState(false);

  useEffect(() => {
    if (isOpen) {
      setEmails(getMockInbox());
      setSelectedEmail(null);
      setShowTRNTooltip(false);
    }
  }, [isOpen]);

  const handleClose = () => {
    if (trnWasViewed && onInboxCloseAfterTRN) {
      onInboxCloseAfterTRN();
    }
    onClose();
  };

  const handleEmailClick = (email) => {
    setSelectedEmail(email);
    if (!email.isRead) {
      const updated = markEmailAsRead(email.id);
      setEmails(updated);
    }
    // Show tooltip for TRN email
    if (email.subject.includes('TRN') || email.subject.includes('Temporary Reference')) {
      setShowTRNTooltip(true);
      setTrnWasViewed(true);
      if (onTRNEmailViewed) {
        onTRNEmailViewed();
      }
    } else {
      setShowTRNTooltip(false);
    }
  };

  const handleDeleteEmail = (e, emailId) => {
    e.stopPropagation(); // Prevent opening the email
    const updated = deleteEmail(emailId);
    setEmails(updated);
    if (selectedEmail && selectedEmail.id === emailId) {
      setSelectedEmail(null);
      setShowTRNTooltip(false);
    }
  };

  const unreadCount = emails.filter(e => !e.isRead).length;

  // Check if current email is TRN email
  const isTRNEmail = selectedEmail && (selectedEmail.subject.includes('TRN') || selectedEmail.subject.includes('Temporary Reference'));

  const formatDate = (dateStr) => {
    const date = new Date(dateStr);
    const now = new Date();
    const diff = now - date;
    
    if (diff < 60000) return 'Just now';
    if (diff < 3600000) return `${Math.floor(diff/60000)}m ago`;
    if (diff < 86400000) return `${Math.floor(diff/3600000)}h ago`;
    return date.toLocaleDateString('en-IN', { day: '2-digit', month: 'short' });
  };

  return (
    <>
      <Dialog open={isOpen} onOpenChange={handleClose}>
        <DialogContent 
          className="sm:max-w-2xl max-h-[85vh] bg-white border-gray-200 p-0 overflow-visible z-[100]"
          data-testid="mock-inbox-modal"
          style={{ zIndex: 100 }}
        >
          {/* Main Email Content */}
          <div className="min-w-0">
            {/* Light Theme Header */}
            <DialogHeader className="bg-gradient-to-r from-blue-600 to-blue-700 px-6 py-4">
              <div className="flex items-center justify-between">
                <div className="flex items-center gap-3">
                  {selectedEmail ? (
                    <Button
                      variant="ghost"
                      size="sm"
                      onClick={() => { setSelectedEmail(null); setShowTRNTooltip(false); }}
                      className="text-white hover:bg-white/20 -ml-2"
                      data-testid="inbox-back-btn"
                    >
                      <ArrowLeft className="w-5 h-5" />
                    </Button>
                  ) : (
                    <div className="w-10 h-10 bg-white/20 rounded-lg flex items-center justify-center">
                      <Inbox className="w-5 h-5 text-white" />
                    </div>
                  )}
                  <div>
                    <DialogTitle className="text-lg text-white">
                      {selectedEmail ? 'Email Details' : 'Mock Inbox'}
                    </DialogTitle>
                    {!selectedEmail && (
                      <p className="text-sm text-blue-100">GST Portal Notifications</p>
                    )}
                  </div>
                </div>
                {!selectedEmail && unreadCount > 0 && (
                  <Badge className="bg-red-500 text-white border-0" data-testid="inbox-unread-badge">
                    {unreadCount} New
                  </Badge>
                )}
              </div>
            </DialogHeader>

        <div className="overflow-y-auto max-h-[65vh] bg-gray-50">
          {selectedEmail ? (
            // Email Detail View - Light Theme
            <div className="p-6 bg-white">
              <div className="mb-6">
                <div className="flex items-start justify-between gap-4">
                  <h3 className="text-lg font-semibold text-gray-900" data-testid="email-subject">
                    {selectedEmail.subject}
                  </h3>
                  <Button
                    variant="ghost"
                    size="sm"
                    onClick={(e) => handleDeleteEmail(e, selectedEmail.id)}
                    className="text-red-500 hover:text-red-600 hover:bg-red-50 flex-shrink-0"
                    data-testid="delete-email-btn"
                  >
                    <Trash2 className="w-4 h-4 mr-1" />
                    Delete
                  </Button>
                </div>
                <div className="flex flex-wrap items-center gap-4 text-sm text-gray-500 mt-2">
                  <span>From: <span className="text-blue-600 font-medium">{selectedEmail.from}</span></span>
                  <span className="hidden sm:inline">•</span>
                  <span className="flex items-center gap-1">
                    <Clock className="w-4 h-4" />
                    {formatDate(selectedEmail.date)}
                  </span>
                </div>
              </div>
              
              <div className="bg-gray-50 rounded-lg p-4 border border-gray-200">
                <pre 
                  className="text-sm text-gray-700 whitespace-pre-wrap font-mono leading-relaxed"
                  data-testid="email-body"
                >
                  {selectedEmail.body}
                </pre>
              </div>
            </div>
          ) : (
            // Email List View - Light Theme
            <div className="bg-white">
              {emails.length === 0 ? (
                <div className="text-center py-16 px-4">
                  <div className="w-16 h-16 bg-gray-100 rounded-full flex items-center justify-center mx-auto mb-4">
                    <Mail className="w-8 h-8 text-gray-400" />
                  </div>
                  <p className="text-gray-600 font-medium">No emails yet</p>
                  <p className="text-sm text-gray-400 mt-1">
                    Complete Part-A of registration to receive your TRN
                  </p>
                </div>
              ) : (
                <div className="divide-y divide-gray-100">
                  {emails.map((email) => (
                    <div
                      key={email.id}
                      className={`relative group w-full text-left p-4 hover:bg-blue-50 transition-colors cursor-pointer ${
                        !email.isRead ? 'bg-blue-50/70' : 'bg-white'
                      }`}
                      onClick={() => handleEmailClick(email)}
                      data-testid={`email-item-${email.id}`}
                    >
                      <div className="flex items-start gap-3">
                        <div className="mt-1 flex-shrink-0">
                          {email.isRead ? (
                            <CheckCircle2 className="w-4 h-4 text-gray-400" />
                          ) : (
                            <Circle className="w-4 h-4 text-blue-500 fill-blue-500" />
                          )}
                        </div>
                        <div className="flex-1 min-w-0">
                          <div className="flex items-center justify-between mb-1 gap-2">
                            <span className={`text-sm ${email.isRead ? 'text-gray-500' : 'text-blue-600 font-medium'}`}>
                              {email.from}
                            </span>
                            <div className="flex items-center gap-2 flex-shrink-0">
                              <span className="text-xs text-gray-400">
                                {formatDate(email.date)}
                              </span>
                              <button
                                onClick={(e) => handleDeleteEmail(e, email.id)}
                                className="opacity-0 group-hover:opacity-100 p-1.5 text-gray-400 hover:text-red-500 hover:bg-red-50 rounded transition-all"
                                data-testid={`delete-btn-${email.id}`}
                              >
                                <Trash2 className="w-4 h-4" />
                              </button>
                            </div>
                          </div>
                          <p className={`text-sm truncate ${email.isRead ? 'text-gray-600' : 'text-gray-900 font-medium'}`}>
                            {email.subject}
                          </p>
                          <p className="text-xs text-gray-400 truncate mt-1">
                            {email.body.slice(0, 100).replace(/\n/g, ' ')}...
                          </p>
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </div>
          )}
        </div>

        {/* Footer hint */}
            <div className="px-4 py-3 bg-gray-50 border-t border-gray-200 text-center">
              <p className="text-xs text-gray-400">
                This is a simulated inbox for learning purposes
              </p>
            </div>
          </div>
        </DialogContent>
      </Dialog>

      {/* TRN Congratulations Tooltip - OUTSIDE the dialog, positioned to the right */}
      {isOpen && showTRNTooltip && isTRNEmail && (
        <div 
          className="fixed top-1/2 -translate-y-1/2 z-[110] animate-in slide-in-from-right duration-300"
          style={{ right: 'calc(50% - 420px - 300px)' }}
        >
          <div className="w-72 bg-gradient-to-br from-blue-600 to-blue-700 text-white p-5 rounded-xl shadow-2xl">
            <div className="flex items-start justify-between mb-3">
              <div className="flex items-center gap-2">
                <PartyPopper className="w-6 h-6 text-yellow-300" />
                <span className="font-bold text-lg">Well Done! 🎉</span>
              </div>
              <button 
                onClick={() => setShowTRNTooltip(false)}
                className="text-white/70 hover:text-white p-1 hover:bg-white/10 rounded"
              >
                <X className="w-4 h-4" />
              </button>
            </div>
            
            <p className="text-blue-100 text-sm mb-4">
              You crushed Part-A! 💪 Now let's finish the job!
            </p>
            
            <div className="space-y-3 mb-4">
              <div className="flex items-start gap-2">
                <span className="bg-white/20 w-6 h-6 rounded-full flex items-center justify-center text-xs font-bold flex-shrink-0">1</span>
                <p className="text-sm text-blue-50">
                  <strong>Copy</strong> your TRN number from this email
                </p>
              </div>
              
              <div className="flex items-start gap-2">
                <span className="bg-white/20 w-6 h-6 rounded-full flex items-center justify-center text-xs font-bold flex-shrink-0">2</span>
                <p className="text-sm text-blue-50">
                  <strong>Close</strong> this inbox
                </p>
              </div>
              
              <div className="flex items-start gap-2">
                <span className="bg-white/20 w-6 h-6 rounded-full flex items-center justify-center text-xs font-bold flex-shrink-0">3</span>
                <div className="text-sm text-blue-50">
                  <p><strong>Select</strong> "Temporary Reference Number (TRN)" at the top of the form</p>
                  <div className="mt-2 p-2 bg-white/10 rounded-lg">
                    <p className="text-xs flex items-center gap-1">
                      <ArrowUpRight className="w-3 h-3" />
                      Look for this option:
                    </p>
                    <p className="text-xs mt-1 font-mono">
                      ○ New Registration<br/>
                      <span className="text-yellow-300">◉ Temporary Reference Number (TRN) ← Here!</span>
                    </p>
                  </div>
                </div>
              </div>
              
              <div className="flex items-start gap-2">
                <span className="bg-white/20 w-6 h-6 rounded-full flex items-center justify-center text-xs font-bold flex-shrink-0">4</span>
                <p className="text-sm text-blue-50">
                  <strong>Paste</strong> TRN, verify OTP & enter Part-B! 🚀
                </p>
              </div>
            </div>
            
            <div className="p-2 bg-green-500/20 rounded-lg border border-green-400/30">
              <p className="text-xs text-green-100">
                🌟 You're doing amazing! Part-B is waiting for you, champ!
              </p>
            </div>
          </div>
        </div>
      )}
    </>
  );
};

export default MockInboxLight;
